import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactsSection } from '../Sections/ContactsSection';
import { InfoViews } from '../InfoViews';
import { CurrentResidenceQuery } from '../../../graphql/graphql';
import { GalleryContainer, GalleryWrapper, infoViewsStyles } from './styled';
import { GallerySlider } from '../../ui/GallerySlider';
import { usePageNumberOnScroll } from '../ItemPhotosGallery/usePageNumberOnScroll';
import { GalleryLocationInfo } from '../Sections/GalleryLocationInfo';
import { GalleryCollage } from './GalleryCollage';
import { FullScreenGallery } from '../../ui/FullScreenGallery';
import { ResidenceGalleryFooter } from './ResidenceGalleryFooter';
import { PhoneButtonClickHandler } from '../BottomPhoneButton/types';
import { ResidenceGalleryHeader } from './ResidenceGalleryHeader';

export interface PhotosGalleryProps {
  residence: Partial<NonNullable<CurrentResidenceQuery['residence']>>;
  phonesShown: string[] | null;
  onClickHandler: PhoneButtonClickHandler;
  priceMin?: number | null;
  currency?: string;
  setIsVisible?: (value: boolean) => void;
  isVisible?: boolean;
}

export const PhotosGallery: React.FC<PhotosGalleryProps> = memo(
  ({
    residence,
    onClickHandler,
    phonesShown = [],
    priceMin,
    currency,
    setIsVisible,
    isVisible
  }) => {
    const { t } = useTranslation();

    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [initialGalleryIndex, setInitialGalleryPhoto] = useState<
      number | null
    >(null);

    const photos = residence.photos || [];
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const { slidesToRender, pageNumber, scrollToNextPhoto, scrollToPrevPhoto } =
      usePageNumberOnScroll(photos, scrollRef);

    const onOpenGallery = (
      e: React.SyntheticEvent<HTMLDivElement>,
      photoNumber = pageNumber
    ) => {
      setInitialGalleryPhoto(photoNumber);
      e.stopPropagation();
      setIsGalleryOpen(true);
    };

    const handleGalleryClose = () => {
      setIsGalleryOpen(false);
    };

    if (!photos?.length) {
      return null;
    }

    const shouldRenderCollage =
      residence.hasCollageGallery && photos.length >= 3;
    const galleryDescription = t('residences.title', {
      name: residence.companyName
    });
    const priceInfo = t('residences.priceMin', { priceMin, currency });

    return (
      <>
        <GalleryContainer>
          <InfoViews hits={residence.hits} styles={infoViewsStyles} />
          <GalleryLocationInfo
            city={residence.city?.name}
            address={residence.address}
            itemsCount={residence.itemsCount}
          />
          <ContactsSection
            {...{ residence, onClickHandler, phonesShown, onOpenGallery }}
          />
          <GalleryWrapper $isCollage $shouldRenderCollage={shouldRenderCollage}>
            <GalleryCollage
              photos={photos}
              onOpenGallery={onOpenGallery}
              galleryDescription={galleryDescription}
            />
          </GalleryWrapper>

          <GalleryWrapper
            $isCollage={false}
            $shouldRenderCollage={shouldRenderCollage}
          >
            <GallerySlider
              photos={slidesToRender}
              description={galleryDescription}
              scrollRef={scrollRef}
              scrollToPrevPhoto={scrollToPrevPhoto}
              scrollToNextPhoto={scrollToNextPhoto}
              onOpenGallery={onOpenGallery}
              shouldRenderBlurredBackground={false}
              showFullScreenButton={false}
              shouldRenderButton
            />
          </GalleryWrapper>
        </GalleryContainer>
        {isGalleryOpen && initialGalleryIndex && (
          <FullScreenGallery
            description={galleryDescription}
            isOpen={isGalleryOpen}
            withoutSafeTopPadding
            photos={photos}
            initialGalleryIndex={initialGalleryIndex}
            footer={
              <ResidenceGalleryFooter
                galleryDescription={galleryDescription}
                priceInfo={priceInfo}
                phones={phonesShown}
                onClickHandler={onClickHandler}
              />
            }
            onClose={handleGalleryClose}
            renderHeader={currentPage => (
              <ResidenceGalleryHeader
                galleryDescription={galleryDescription}
                priceInfo={priceInfo}
                phones={phonesShown}
                pageNumber={currentPage}
                photosLength={photos.length}
                onClickHandler={onClickHandler}
                onClose={handleGalleryClose}
                setIsVisible={setIsVisible}
                isVisible={isVisible}
              />
            )}
          />
        )}
      </>
    );
  }
);

PhotosGallery.displayName = 'ItemPhotosGallery';
